import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import 'rxjs/add/operator/catch';
import {
    AuthorDataService,
    AuthorEntirePollResponse,
    AuthorPollResponse,
    BasicResponse,
    BtPoll,
    DataRequestOptions,
    EntirePoll,
    EventObject,
    EventService,
    LoggerService,
    PollListResponse,
    PollResponse,
    QuestionExt,
    Questions,
    QuestionsResponse,
    ScriptQuestSets,
    Scripts,
    StoreEventsEnum
} from '../../../modules/hbs-services';

/**
 * Note: Used by authenticated users only as DataRequestOptions.anonymous = false
 */
@Injectable()
export class PollService {

    constructor(private authorDataService: AuthorDataService,
                private eventService: EventService,
                private logger: LoggerService) {
    }

    loadEntirePoll(pollId: any): Observable<EntirePoll> {
        return this.authorDataService.getAuthorEntirePoll(pollId, this.getDataRequestOptions())
            .pipe(map(data => EntirePoll.deserialize(data.record)));
    }

    insertBtPoll(poll: BtPoll, isPublic: boolean): Observable<AuthorEntirePollResponse> {
        return this.authorDataService.authorInsertBtPoll(poll, isPublic, this.getDataRequestOptions())
            .pipe(map(this.validateAndDeserialize<BasicResponse, AuthorEntirePollResponse>(AuthorEntirePollResponse,
                StoreEventsEnum.EDIT_POLL_VALIDATION_ERRORS, 'poll'))
            );
    }

    publishPoll(pollId: number): Observable<number> {
        return this.authorDataService.publishPoll(pollId, this.getDataRequestOptions())
            .pipe(map(data => data));

    }

    unpublishPoll(pollId: number): Observable<number> {
        return this.authorDataService.unpublishPoll(pollId, this.getDataRequestOptions())
            .pipe(map(data => data));

    }

    updateBtPoll(poll: BtPoll, pollId: number, updateAllFields: boolean): Observable<AuthorPollResponse> {
        return this.authorDataService.updateAuthorPoll(poll, pollId, updateAllFields, this.getDataRequestOptions())
            .pipe(map(this.validateAndDeserialize<PollResponse, AuthorPollResponse>(AuthorPollResponse,
                StoreEventsEnum.EDIT_POLL_VALIDATION_ERRORS, 'poll'))
            );

    }

    changePollAudience(pollId: any, makePublic: boolean): Observable<EntirePoll> {
        return this.authorDataService.changePollAudience(pollId, makePublic, this.getDataRequestOptions())
            .pipe(map(data => EntirePoll.deserialize(data.record)));
    }

    deletePoll(pollId: number): Observable<AuthorPollResponse> {
        return this.authorDataService.deletePoll(pollId, this.getDataRequestOptions())
            .pipe(map(this.validateAndDeserialize<PollResponse, AuthorPollResponse>(AuthorPollResponse,
                StoreEventsEnum.EDIT_POLL_VALIDATION_ERRORS, 'poll')));
    }

    deletePollResults(pollId: number): Observable<string> {
        return this.authorDataService.deletePollResults(pollId, this.getDataRequestOptions());
    }

    copyPoll(pollId: number, newTitle: string): Observable<PollListResponse> {
        return this.authorDataService.copyPoll(pollId, newTitle, this.getDataRequestOptions());
    }

    updateQuestions(questions: QuestionExt[], pollId: number): Observable<Questions> {
        return this.authorDataService.updateAuthorPollQuestions(questions, pollId, this.getDataRequestOptions())
            .pipe(map((data: QuestionsResponse) => Questions.deserialize(data.records)));

    }

    replaceAllQuestionsAndUpdateScriptQuestSets(questions: QuestionExt[], scriptQuestSets: any,
                                                questNumPairs: any[], keepResponses: boolean, pollId: number): Observable<Questions> {
        return this.authorDataService.replaceAllQuestionsAndUpdateScriptQuestSets(questions, scriptQuestSets,
            questNumPairs, keepResponses, pollId, this.getDataRequestOptions())
            .pipe(map(this.validateAndDeserialize<QuestionsResponse, Questions>(Questions,
                StoreEventsEnum.EDIT_QUESTION_VALIDATION_ERRORS, 'records')));

    }

    deleteQuestions(questNums: number[], pollId: number): Observable<Questions> {
        return this.authorDataService.deleteAuthorPollQuestions(questNums, pollId, this.getDataRequestOptions())
            .pipe(map(this.validateAndDeserialize<QuestionsResponse, Questions>(Questions,
                StoreEventsEnum.VALIDATION_ERRORS, 'records')));

    }

    replaceAllScriptQuestSets(scriptQuestSets: ScriptQuestSets,
                              questNumPairs: any[], keepResponses: boolean, pollId: number | any) {
        return this.authorDataService.replaceAllScriptQuestSets(scriptQuestSets,
            questNumPairs, keepResponses, pollId, this.getDataRequestOptions())
            .pipe(map((data: any) => ScriptQuestSets.deserialize(data.records)));

    }

    replaceAllScriptsAndScriptQuestSets(scripts: Scripts, scriptQuestSets: ScriptQuestSets,
                                        questNumPairs: any[], keepResponses: boolean, pollId: number) {
        return this.authorDataService.replaceAllScriptsAndScriptQuestSets(scripts, scriptQuestSets,
            questNumPairs, keepResponses, pollId, this.getDataRequestOptions())
            .pipe(map((data: any) => EntirePoll.deserialize(data.record)));

    }

    emailResponsesToMe(pollId: number): Observable<string> {
        return this.authorDataService.emailResponsesToMe(pollId, this.getDataRequestOptions());

    }

    private getDataRequestOptions(): DataRequestOptions {

        const options: DataRequestOptions = new DataRequestOptions(false, null/*token*/, '1'/*lockOnce*/);

        return options;

    }

    // e.g. https://angular.io/tutorial/toh-pt6
    private handleError<T>(operation = 'operation', result?: T) {
        return (error: any): Observable<T> => {

            this.logger.error(error); // log to console instead

            // better job of transforming error for user consumption
            this.logger.log(`${operation} failed: ${error.message}`);

            // Let the app keep running by returning an empty result.
            return Observable.of(result as T);
        };
    }

    private validateAndDeserialize<IN, T>(classT: any, storeEvent: StoreEventsEnum, payloadKey: string) {
        return (data: IN): T => {
            const obj: any = classT.deserialize(data);
            if (obj['error'] || obj['errors']) {
                obj['_isValidation'] = true;
                const payload = {};
                payload[payloadKey] = obj;
                this.eventService.emit(new EventObject(storeEvent.toString(), payload));
            }
            return obj as T;
        };
    }

}
